import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { createElement as _createElement } from "@emotion/react";
import { Chip } from "./Chip";
import { Grid } from "./Grid";
import { MenuItem } from "./MenuItem";
import { TextField } from "./TextField";
import { FloatingFocusManager, FloatingPortal, autoUpdate, flip, offset, size, useDismiss, useFloating, useInteractions, useListNavigation, useRole, } from "@floating-ui/react";
import clsx from "clsx";
import xor from "lodash/xor";
import { useEffect, useRef, useState, } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList } from "react-window";
import { MenuPopper } from "./MenuPopper";
import { AddCircleOutline, CloseOutline, ExpandMoreOutline } from "../icons/icons";
import { SlotsProvider } from "../providers/SlotsProvider";
import { mergeSx } from "../system";
import { Typography } from "./Typography";
export const autoCompleteClasses = {
    root: "MinervaAutoComplete-root",
    input: "MinervaAutoComplete-input",
    "expand-icon": "MinervaAutoComplete-expandIcon",
    "multiselect-chip": "MinervaAutoComplete-multiselectChip",
    dropdown: "MinervaAutoComplete-dropdown",
    option: "MinervaAutoComplete-option",
    "chips-container": "MinervaAutoComplete-chipsContainer",
    "new-value-option": "MinervaAutoComplete-newValueOption",
};
export const autoCompleteSlots = {
    option: MenuItem,
    expandIcon: ExpandMoreOutline,
    multiselectChip: Chip,
    dropdown: MenuPopper,
    newValueOption: MenuItem,
};
const filterOptions = (options, inputValue, singleSelect) => {
    if (!options) {
        return [];
    }
    const lowerCaseInput = inputValue.toLowerCase();
    if (singleSelect) {
        if (options.find((item) => item.label?.toLocaleLowerCase() === lowerCaseInput)) {
            return options.sort((a, b) => a.label?.toLowerCase() === lowerCaseInput
                ? -1
                : b.label?.toLowerCase() === lowerCaseInput
                    ? 1
                    : 0);
        }
        return options.filter((item) => item.label?.toLowerCase().includes(lowerCaseInput));
    }
    return options
        .filter((item) => item.label?.toLowerCase().includes(lowerCaseInput))
        .sort((a, b) => a.label?.toLowerCase() === lowerCaseInput
        ? -1
        : b.label?.toLowerCase() === lowerCaseInput
            ? 1
            : 0);
};
/**
 * The AutoComplete component is a text input that suggests options based on user input.
 *
 * NOTE: This component virtualizes the list of options. After 50 options, keyboard navigation is turned off to prevent heavy rerenders, so it is recommended to use it with a reasonable amount of options.
 */
export const AutoComplete = ({ open: _open = false, value, selectedValues: _selectedValues, multiple, disablePortal = false, disableFloating = false, onChange, onDeselect, onFocus, onKeyDown, options, optionHeight = 44, onOptionSelect, allowNewValues = false, onClickNewValue, slots, slotProps, className, endNode, bottomNode, ...rest }) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value ?? "");
    const [selectedValues, setSelectedValues] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const singleSelectOnChange = onChange;
    const multiSelectOnChange = onChange;
    const navigationRef = useRef([]);
    const menuPaddingBuffer = 8;
    const { refs, floatingStyles, context } = useFloating({
        whileElementsMounted: autoUpdate,
        open,
        onOpenChange: setOpen,
        middleware: [
            offset(4),
            flip({ padding: 4 }),
            size({
                apply({ rects, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${availableHeight * 0.85}px`,
                        width: `${rects.reference.width}px`,
                    });
                },
            }),
        ],
    });
    const role = useRole(context, { role: "listbox" });
    const dismiss = useDismiss(context);
    const listNav = useListNavigation(context, {
        listRef: navigationRef,
        activeIndex,
        onNavigate: setActiveIndex,
        virtual: true,
        focusItemOnHover: true,
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        role,
        dismiss,
        listNav,
    ]);
    const handleChange = (event) => {
        const value = event.target.value;
        setInputValue(value);
        if (value) {
            setOpen(true);
            setActiveIndex(null);
        }
        if (!multiple) {
            singleSelectOnChange?.(event, value);
        }
    };
    const handleFocus = (event) => {
        setOpen(true);
        setActiveIndex(null);
        onFocus?.(event);
    };
    useEffect(() => {
        setOpen(_open);
    }, [_open]);
    useEffect(() => {
        setInputValue(value ?? "");
        !!multiple && setSelectedValues(_selectedValues ?? []);
    }, [value, _selectedValues, multiple]);
    useEffect(() => {
        setFilteredOptions(filterOptions(options, inputValue, !multiple));
    }, [options, inputValue]);
    return (_jsx(SlotsProvider, { baseSlots: autoCompleteSlots, slots: slots, children: ({ option: Option, expandIcon: ExpandIcon, multiselectChip: MultiselectChip, dropdown: Dropdown, newValueOption: NewValueOption, ...remainingSlots }) => {
            const menu = (_jsx(FloatingFocusManager, { context: context, initialFocus: -1, visuallyHiddenDismiss: true, returnFocus: !!multiple, children: _jsx(Dropdown, { ...getFloatingProps({
                        ref: refs.setFloating,
                        style: disableFloating ? undefined : floatingStyles,
                    }), ...slotProps?.dropdown, className: clsx([
                        autoCompleteClasses.dropdown,
                        slotProps?.dropdown?.className,
                    ]), sx: mergeSx({
                        boxShadow: disableFloating ? "none" : undefined,
                        // LIMITATION: The height of the menu items is assumed to be 50px. Doing this prevents the need to measure each item's height in a pre-render pass.
                        height: filteredOptions.length > 0
                            ? `${filteredOptions.length * optionHeight +
                                menuPaddingBuffer * 2}px`
                            : "auto",
                        "& .ReactVirtualized__Grid__innerScrollContainer": {
                            scrollbarGutter: "stable",
                        },
                    }, slotProps?.dropdown?.sx), children: filteredOptions.length > 0 ? (filteredOptions.length < 50 ? (filteredOptions.map((item, index) => (_createElement(Option, { ...getItemProps({
                            ref: (node) => {
                                navigationRef.current[index] = node;
                            },
                        }), key: item.label, role: "option", selected: (!!multiple &&
                            item.label &&
                            selectedValues.includes(item?.label)) ||
                            (!multiple && inputValue === item.label), "aria-selected": activeIndex === index ||
                            (!!multiple &&
                                item.label &&
                                selectedValues.includes(item?.label)) ||
                            (!multiple && inputValue === item.label), focused: activeIndex === index, label: item?.label, ...item, ...slotProps?.option, className: clsx([
                            autoCompleteClasses.option,
                            slotProps?.option?.className,
                        ]), onClick: (e) => {
                            e.stopPropagation();
                            if (item.label) {
                                if (!multiple) {
                                    setInputValue(item.label);
                                    singleSelectOnChange?.(undefined, item.label);
                                    setOpen(false);
                                }
                                else {
                                    const newValues = xor(selectedValues, [
                                        item.label,
                                    ]);
                                    setInputValue("");
                                    multiSelectOnChange?.(undefined, newValues);
                                    setSelectedValues(newValues);
                                }
                            }
                            item?.onClick?.(e);
                            onOptionSelect?.(item);
                            slotProps?.option?.onClick?.(e);
                        } })))) : (_jsx(AutoSizer, { children: ({ height, width }) => (_jsx(VariableSizeList, { width: width, height: height, itemCount: filteredOptions.length, itemSize: () => optionHeight, children: ({ index, style }) => {
                                const item = filteredOptions[index];
                                return item ? (_createElement(Option, { ...getItemProps({
                                        ref: (node) => {
                                            navigationRef.current[index] =
                                                node;
                                        },
                                    }), key: item.label, role: "option", selected: (!!multiple &&
                                        item.label &&
                                        selectedValues.includes(item?.label)) ||
                                        (!multiple &&
                                            inputValue === item.label), "aria-selected": activeIndex === index ||
                                        (!!multiple &&
                                            item.label &&
                                            selectedValues.includes(item?.label)) ||
                                        (!multiple &&
                                            inputValue === item.label), focused: activeIndex === index, label: item?.label, ...item, ...slotProps?.option, style: {
                                        ...item.style,
                                        ...style,
                                    }, className: clsx([
                                        autoCompleteClasses.option,
                                        slotProps?.option?.className,
                                    ]), onClick: (e) => {
                                        e.stopPropagation();
                                        if (item.label) {
                                            if (!multiple) {
                                                setInputValue(item.label);
                                                singleSelectOnChange?.(undefined, item.label);
                                                setOpen(false);
                                            }
                                            else {
                                                const newValues = xor(selectedValues, [item.label]);
                                                setInputValue("");
                                                multiSelectOnChange?.(undefined, newValues);
                                                setSelectedValues(newValues);
                                            }
                                        }
                                        item?.onClick?.(e);
                                        onOptionSelect?.(item);
                                        slotProps?.option?.onClick?.(e);
                                    } })) : null;
                            } })) }))) : allowNewValues && inputValue.length > 0 ? (_jsx(NewValueOption, { ...getItemProps({
                            ref: (node) => {
                                navigationRef.current = [node];
                            },
                        }), role: "option", selected: true, "aria-selected": true, focused: true, label: `${inputValue}`, color: "info", startNode: _jsx(AddCircleOutline, { size: "sm" }), ...slotProps?.newValueOption, className: clsx([
                            autoCompleteClasses["new-value-option"],
                            slotProps?.newValueOption?.className,
                        ]), onClick: (e) => {
                            setInputValue("");
                            onClickNewValue?.(inputValue);
                            !multiple && setOpen(false);
                            e.stopPropagation();
                            slotProps?.newValueOption?.onClick?.(e);
                        } })) : (_jsx(Typography, { color: "accent", variant: "body2", sx: {
                            padding: 2,
                        }, children: "No Options" })) }) }));
            return (_jsxs(_Fragment, { children: [_jsx(TextField, { ...getReferenceProps({
                            onChange: handleChange,
                            onFocus: handleFocus,
                            value: inputValue,
                            "aria-autocomplete": "list",
                            onKeyDown(event) {
                                if (event.key === "Enter" &&
                                    activeIndex != null &&
                                    filteredOptions.length > 0 &&
                                    filteredOptions[activeIndex]) {
                                    if (!multiple) {
                                        setInputValue(filteredOptions[activeIndex]?.label ?? "");
                                        setActiveIndex(null);
                                        setOpen(false);
                                    }
                                    else {
                                        const value = filteredOptions[activeIndex]?.label;
                                        const newValues = value
                                            ? xor(selectedValues, [value])
                                            : selectedValues;
                                        setInputValue("");
                                        multiSelectOnChange?.(undefined, newValues);
                                        setSelectedValues(newValues);
                                    }
                                    onOptionSelect?.(filteredOptions[activeIndex]);
                                }
                                else if (allowNewValues && event.key === "Enter") {
                                    onClickNewValue?.(inputValue);
                                    !multiple && setOpen(false);
                                }
                                onKeyDown?.(event);
                            },
                        }), rootRef: refs.setReference, endNode: endNode ? (endNode) : (_jsx(ExpandIcon, { ...slotProps?.expandIcon, sx: {
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s",
                                ...slotProps?.expandIcon?.sx,
                            }, className: clsx([
                                autoCompleteClasses["expand-icon"],
                                slotProps?.expandIcon?.className,
                            ]) })), ...rest, slots: remainingSlots, slotProps: slotProps, className: clsx([autoCompleteClasses.input, className]), bottomNode: !!multiple && selectedValues.length ? (_jsx(Grid, { container: true, gap: 1, className: autoCompleteClasses["chips-container"], children: selectedValues.map((value) => {
                                const selectedOption = options?.find((o) => o.label === value);
                                return (_jsx(MultiselectChip, { variant: "tonal", density: "dense", color: "info", startNode: selectedOption?.startNode, endNode: _jsx(CloseOutline, { size: "sm" }), ...slotProps?.multiselectChip, className: clsx([
                                        autoCompleteClasses["multiselect-chip"],
                                        slotProps?.multiselectChip?.className,
                                    ]), onClick: (e) => {
                                        const newValues = selectedValues.filter((v) => v !== value);
                                        multiSelectOnChange?.(undefined, newValues);
                                        setSelectedValues(newValues);
                                        onDeselect?.(value);
                                        slotProps?.multiselectChip?.onClick?.(e);
                                    }, children: value }, value));
                            }) })) : (bottomNode) }), open &&
                        (disablePortal || disableFloating ? (menu) : (_jsx(FloatingPortal, { children: menu })))] }));
        } }));
};
//# sourceMappingURL=AutoComplete.js.map